/**
 * Auto-fit grid of partner cards (initial square + name + description).
 * Ported from ThemeMaker's {@code blocks/grid/PartnersGrid}.
 *
 * <p>Cards fit a {@code minmax(280px, 1fr)} track so the grid reflows
 * from 4 columns down to 1 as the viewport narrows.
 * <p>When {@code infiniteSlider} is true, cards are rendered as a
 * continuously scrolling marquee instead of a static grid.
 * <p>Logos share one box height ({@code logoHeight}) with a per-partner
 * {@code logoScale} nudge, so a wall of mixed-aspect artwork reads at a single
 * optical size. A {@code seeAllLabel}/{@code seeAllHref} pair puts a "see every
 * partner" link beside the heading for tenants with a long partner list.
 * <p>In dark mode every variant renders its logos as a white silhouette so
 * light-page artwork stays legible — see {@link DARK_LOGO_CSS}.
 */
import type { CSSProperties } from 'react';
import { PortalLink, useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { cardReset } from '../_shared/styles';
import type { PaddingBox } from '../_shared/resolvePadding';

type Localized = string | Record<string, string>;

interface Partner {
  name?: Localized;
  description?: Localized;
  initial?: string;
  logo?: string;
  tone?: string;
  /**
   * Optical-size correction, {@code 1} = no change. Logos are normalised to a
   * common box height, but a square emblem reads bigger than a wide wordmark
   * at the same height — nudge the outliers (0.8 / 1.15) so the wall looks
   * evenly weighted. Purely visual: the box height stays the same, so
   * baselines and row alignment never shift.
   */
  logoScale?: number;
}

export interface PartnersGridProps {
  title?: Localized;
  kicker?: Localized;
  subtitle?: Localized;
  partners?: Partner[];
  infiniteSlider?: boolean;
  /**
   * Card style. {@code 'card'} (default) renders the horizontal logo + name +
   * description card. {@code 'logoHover'} renders large, prominent logos in a
   * grid (or marquee) and reveals the partner label only on hover.
   * {@code 'logoLabel'} keeps the same large-logo wall but leaves the label
   * permanently under the logo: every logo is greyscaled so the wall reads as
   * one tone, and hover lifts a single logo back into colour at a slightly
   * larger size.
   */
  variant?: 'card' | 'logoHover' | 'logoLabel';
  /**
   * Logo rendering in the {@code logoHover} variant. {@code 'mono'} (default)
   * flattens every logo into one ink tone for a cohesive wall;
   * {@code 'original'} keeps the source artwork's own colors, which reads with
   * the most contrast when the partners are recognisable brands.
   */
  logoColor?: 'mono' | 'original';
  /** Shared logo box height in px (default 56) — the optical baseline. */
  logoHeight?: number;
  /** Label for the "see every partner" link beside the heading. */
  seeAllLabel?: Localized;
  /** Target of the link; the link only renders when both label and href exist. */
  seeAllHref?: string;
  /** Cap how many partners the static grid shows (the rest live behind the link). */
  maxVisible?: number;
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

/** Per-card CSS custom properties that carry the optical-size correction. */
function logoVars(p: Partner): CSSProperties {
  return p.logoScale && p.logoScale !== 1
    ? ({ '--sb-logo-scale': String(p.logoScale) } as CSSProperties)
    : {};
}

function PartnerCard({ p }: { p: Partner }) {
  return (
    <Card
      style={{
        ...cardReset,
        background: 'var(--color-surface-card, #fff)',
        borderRadius: 'var(--radius-md, 12px)',
        padding: 'var(--space-5, 20px)',
        display: 'flex',
        gap: 16,
        alignItems: 'center',
      }}
    >
      {/* Fixed 40px box for every entry — logo or monogram — so the artwork is
          large enough to read and every card's text starts on the same axis. */}
      <div
        style={{
          flex: '0 0 40px',
          height: 40,
          background: p.logo ? 'transparent' : p.tone || 'var(--color-brand-500, #3b82f6)',
          color: 'var(--color-text-on-brand, #fff)',
          borderRadius: 'var(--radius-sm, 8px)',
          display: 'grid',
          placeItems: 'center',
          fontSize: 'var(--font-size-lg, 18px)',
          fontWeight: 700,
          fontFamily: 'var(--font-family-display, inherit)',
          overflow: 'hidden',
        }}
      >
        {p.logo ? (
          <img
            className="sb-partner-cardlogo"
            src={p.logo}
            alt={tx(p.name) ?? ''}
            style={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              transform: p.logoScale && p.logoScale !== 1 ? `scale(${p.logoScale})` : undefined,
            }}
          />
        ) : (
          p.initial || '·'
        )}
      </div>
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-base, 15px)',
            fontWeight: 600,
            color: 'var(--color-text-primary, #18181b)',
          }}
        >
          {tx(p.name)}
        </div>
        {p.description && (
          <p
            style={{
              margin: 'var(--space-1, 4px) 0 0',
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {tx(p.description)}
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * Large-logo variant. In {@code mono} mode each logo is rendered as a
 * monochrome cut-out (a CSS mask fills the logo's shape with one ink tone, so
 * mixed source colors collapse into a single cohesive partner wall); in
 * {@code original} mode the source artwork keeps its own colors. Either way
 * every logo sits in the same fixed-height box, so the wall reads as one
 * optical line. The partner name + description fade in as an overlay on hover.
 */
function PartnerLogoCard({ p, logoColor }: { p: Partner; logoColor?: 'mono' | 'original' }) {
  const name = tx(p.name) ?? '';
  return (
    <div className="sb-partner-logocard" style={logoVars(p)}>
      {p.logo ? (
        logoColor === 'original' ? (
          <img className="sb-partner-logocard__img" src={p.logo} alt={name} />
        ) : (
          // Masked <span> instead of <img>: the logo's alpha channel masks a
          // flat fill, giving a true negative/cut-out silhouette regardless of
          // the asset's original colors. Fill color comes from the CSS class.
          <span
            className="sb-partner-logocard__logo"
            role="img"
            aria-label={name}
            style={{
              WebkitMaskImage: `url("${p.logo}")`,
              maskImage: `url("${p.logo}")`,
            }}
          />
        )
      ) : (
        <div
          className="sb-partner-logocard__monogram"
          style={{ background: p.tone || 'var(--color-brand-500, #3b82f6)' }}
        >
          {p.initial || '·'}
        </div>
      )}
      <div className="sb-partner-logocard__label">
        <div>
          <div className="sb-partner-logocard__name">{name}</div>
          {p.description && <p className="sb-partner-logocard__desc">{tx(p.description)}</p>}
        </div>
      </div>
    </div>
  );
}

/**
 * Always-labelled logo variant. Same optical rules as {@link PartnerLogoCard}
 * — one shared box height, per-partner {@code logoScale} nudge — but the name
 * sits permanently under the logo instead of hiding behind a hover overlay, so
 * the wall is readable at a glance. Every logo is greyscaled at rest so mixed
 * brand palettes collapse into one calm tone; hovering a card returns that one
 * logo to full colour and scales it up a touch as the affordance.
 */
function PartnerLogoLabelCard({ p }: { p: Partner }) {
  const name = tx(p.name) ?? '';
  return (
    <div className="sb-partner-logolabel" style={logoVars(p)}>
      <div className="sb-partner-logolabel__box">
        {p.logo ? (
          <img className="sb-partner-logolabel__img" src={p.logo} alt={name} />
        ) : (
          <div
            className="sb-partner-logolabel__monogram"
            style={{ background: p.tone || 'var(--color-brand-500, #3b82f6)' }}
          >
            {p.initial || '·'}
          </div>
        )}
      </div>
      <div className="sb-partner-logolabel__name">{name}</div>
      {p.description && <p className="sb-partner-logolabel__desc">{tx(p.description)}</p>}
    </div>
  );
}

const LOGO_LABEL_VARIANT_CSS = `
  .sb-partners-logolabelgrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--space-8, 32px) var(--space-6, 24px);
    align-items: start;
  }
  .sb-partner-logolabel {
    display: grid;
    justify-items: center;
    gap: var(--space-3, 12px);
    padding: var(--space-5, 20px) var(--space-4, 16px);
    text-align: center;
  }
  /* The logo lives in its own fixed-height box so the labels underneath all
     start on the same axis no matter how tall each piece of artwork is. */
  .sb-partner-logolabel__box {
    display: grid;
    place-items: center;
    width: 100%;
    height: var(--sb-logo-box-h, 56px);
  }
  /* Greyscale at rest keeps the wall cohesive; hover returns the one logo the
     pointer is on to full colour and nudges it larger.
     The height is set explicitly (not max-height: 100%) because a percentage
     max-height doesn't constrain an auto-placed grid item — a square emblem
     would grow to its max-width, spill out of the box and sit on top of the
     label. Fixed height + object-fit: contain letterboxes every aspect ratio
     into the same optical line instead. */
  .sb-partner-logolabel__img {
    width: 100%;
    max-width: 160px;
    height: var(--sb-logo-box-h, 56px);
    object-fit: contain;
    object-position: center;
    filter: grayscale(1);
    opacity: 0.75;
    transform: scale(var(--sb-logo-scale, 1));
    transition: filter 0.25s ease, opacity 0.25s ease, transform 0.25s ease;
  }
  .sb-partner-logolabel:hover .sb-partner-logolabel__img {
    filter: grayscale(0);
    opacity: 1;
    transform: scale(calc(var(--sb-logo-scale, 1) * 1.08));
  }
  /* Square monogram sized off the box height for the same reason — it has to
     fit the shared optical line, not push past it. */
  .sb-partner-logolabel__monogram {
    height: 100%;
    width: auto;
    aspect-ratio: 1;
    border-radius: var(--radius-md, 12px);
    display: grid;
    place-items: center;
    font-size: 26px;
    font-weight: 700;
    color: var(--color-text-on-brand, #fff);
    font-family: var(--font-family-display, inherit);
    filter: grayscale(1);
    opacity: 0.75;
    transition: filter 0.25s ease, opacity 0.25s ease, transform 0.25s ease;
  }
  .sb-partner-logolabel:hover .sb-partner-logolabel__monogram {
    filter: grayscale(0);
    opacity: 1;
    transform: scale(1.08);
  }
  .sb-partner-logolabel__name {
    font-size: var(--font-size-base, 15px);
    font-weight: 600;
    line-height: 1.35;
    color: var(--color-text-secondary, #52525b);
    transition: color 0.25s ease;
  }
  .sb-partner-logolabel:hover .sb-partner-logolabel__name {
    color: var(--color-text-primary, #18181b);
  }
  .sb-partner-logolabel__desc {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: var(--color-text-tertiary, #71717a);
  }
  @media (prefers-reduced-motion: reduce) {
    .sb-partner-logolabel__img,
    .sb-partner-logolabel__monogram,
    .sb-partner-logolabel__name { transition: none; }
    .sb-partner-logolabel:hover .sb-partner-logolabel__img,
    .sb-partner-logolabel:hover .sb-partner-logolabel__monogram { transform: none; }
  }
`;

const LOGO_VARIANT_CSS = `
  .sb-partners-logogrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--space-8, 32px) var(--space-6, 24px);
    align-items: center;
  }
  .sb-partner-logocard {
    position: relative;
    background: transparent;
    border: 0;
    border-radius: var(--radius-md, 12px);
    padding: var(--space-6, 24px) var(--space-4, 16px);
    display: grid;
    place-items: center;
    min-height: 120px;
    overflow: hidden;
  }
  /* Cut-out logo: the mask (set inline per partner) knocks the fill into the
     logo's shape; --sb-logo-fill is the single ink tone the whole wall shares.
     Defaults to text-secondary (not tertiary) so the silhouettes stay legible
     against the page instead of washing out; goes full text-primary on hover.
     Height comes from --sb-logo-box-h, shared by every card, and --sb-logo-scale
     is the per-partner optical nudge inside that box. */
  .sb-partner-logocard__logo {
    width: 100%;
    max-width: 160px;
    height: var(--sb-logo-box-h, 56px);
    background-color: var(--sb-logo-fill, var(--color-text-secondary, #52525b));
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: contain;
    mask-size: contain;
    transform: scale(var(--sb-logo-scale, 1));
    transition: background-color 0.25s ease, opacity 0.25s ease, transform 0.25s ease;
  }
  /* Original-color logo: same box, same optical rules, source artwork intact. */
  .sb-partner-logocard__img {
    width: 100%;
    max-width: 160px;
    height: var(--sb-logo-box-h, 56px);
    object-fit: contain;
    object-position: center;
    transform: scale(var(--sb-logo-scale, 1));
    transition: opacity 0.25s ease, transform 0.25s ease;
  }
  .sb-partner-logocard:hover .sb-partner-logocard__img {
    transform: scale(calc(var(--sb-logo-scale, 1) * 0.92));
    opacity: 0.12;
  }
  .sb-partner-logocard__monogram {
    width: 72px;
    height: 72px;
    border-radius: var(--radius-md, 12px);
    display: grid;
    place-items: center;
    font-size: 32px;
    font-weight: 700;
    color: var(--color-text-on-brand, #fff);
    font-family: var(--font-family-display, inherit);
    transition: transform 0.25s ease, opacity 0.25s ease;
  }
  .sb-partner-logocard:hover .sb-partner-logocard__logo {
    background-color: var(--color-text-primary, #18181b);
    transform: scale(calc(var(--sb-logo-scale, 1) * 0.92));
    opacity: 0.12;
  }
  .sb-partner-logocard:hover .sb-partner-logocard__monogram {
    transform: scale(0.9);
    opacity: 0.12;
  }
  .sb-partner-logocard__label {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    padding: var(--space-4, 16px);
    text-align: center;
    opacity: 0;
    transition: opacity 0.25s ease;
    pointer-events: none;
  }
  .sb-partner-logocard:hover .sb-partner-logocard__label {
    opacity: 1;
  }
  .sb-partner-logocard__name {
    font-size: var(--font-size-lg, 18px);
    font-weight: 700;
    color: var(--color-text-primary, #18181b);
  }
  .sb-partner-logocard__desc {
    margin: var(--space-1, 4px) 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: var(--color-text-secondary, #71717a);
  }
  @media (prefers-reduced-motion: reduce) {
    .sb-partner-logocard__logo,
    .sb-partner-logocard__img,
    .sb-partner-logocard__monogram,
    .sb-partner-logocard__label { transition: none; }
  }
`;

/* Dark mode, all three variants. Partner artwork is authored for light pages
   — dark ink on a transparent ground — so on a dark surface it sinks into the
   background. A brightness lift can't rescue it (multiplying black is still
   black), so the artwork is flattened and flipped instead:
   `brightness(0)` collapses every colour to black, `invert(1)` turns that into
   a white silhouette. That is the same cut-out reading the mono mask already
   produces in the logoHover variant, so the wall stays consistent across
   variants. Trade-off: brand colours don't survive on a dark page, including
   in `logoColor: 'original'` — legibility wins there, since an unreadable
   logo carries no brand at all. Applied via the portal's
   `[data-theme="dark"]`/`.dark` contract (see Testimonials, MetricRow). */
const DARK_LOGO_CSS = `
  :where([data-theme="dark"], .dark) .sb-partner-cardlogo {
    filter: brightness(0) invert(1);
  }
  /* Mono cut-out already paints a flat fill — just swap the ink for near-white
     rather than filtering it, and go full white on hover. */
  :where([data-theme="dark"], .dark) .sb-partner-logocard__logo {
    background-color: var(--sb-logo-fill, rgba(255, 255, 255, 0.88));
  }
  :where([data-theme="dark"], .dark) .sb-partner-logocard:hover .sb-partner-logocard__logo {
    background-color: #fff;
  }
  /* The hover rule in LOGO_VARIANT_CSS is more specific, so it still wins and
     fades the logo out behind the label. */
  :where([data-theme="dark"], .dark) .sb-partner-logocard__img {
    filter: brightness(0) invert(1);
    opacity: 0.88;
  }
  /* logoLabel stays greyscale-at-rest by construction here — the silhouette is
     already one tone — so hover keeps the white ink and signals with opacity
     and scale instead of returning to colour. */
  :where([data-theme="dark"], .dark) .sb-partner-logolabel__img {
    filter: brightness(0) invert(1);
    opacity: 0.8;
  }
  :where([data-theme="dark"], .dark) .sb-partner-logolabel:hover .sb-partner-logolabel__img {
    filter: brightness(0) invert(1);
    opacity: 1;
  }
  /* Monograms are solid brand-tone squares with white type — they read fine on
     dark, they were just dimmed by the resting greyscale. Lift the rest state. */
  :where([data-theme="dark"], .dark) .sb-partner-logolabel__monogram {
    opacity: 0.85;
  }
`;

/* Same see-all treatment the other section blocks use (LearningJourneyPlans,
   ActivityRow): brand-tinted 14px/600 label, underline on hover, and an arrow
   that slides right so the link reads as an affordance, not a caption. */
const LINK_CSS = `
  .sb-partners-seeall {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
    color: var(--color-brand-600, #2563eb);
  }
  .sb-partners-seeall:hover { text-decoration: underline; }
  .sb-partners-seeall-arrow { transition: transform 160ms ease; }
  .sb-partners-seeall:hover .sb-partners-seeall-arrow { transform: translateX(3px); }
  @media (prefers-reduced-motion: reduce) {
    .sb-partners-seeall-arrow { transition: none; }
  }
`;

const SLIDER_CSS = `
  .sb-partners-slider {
    overflow: hidden;
    position: relative;
    padding: 8px 0;
  }
  .sb-partners-slider::before,
  .sb-partners-slider::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 80px;
    z-index: 1;
    pointer-events: none;
  }
  .sb-partners-slider::before {
    left: 0;
    background: linear-gradient(to right, var(--color-surface-page, #fff), transparent);
  }
  .sb-partners-slider::after {
    right: 0;
    background: linear-gradient(to left, var(--color-surface-page, #fff), transparent);
  }
  .sb-partners-track {
    display: flex;
    align-items: stretch;
    width: max-content;
    gap: var(--space-4, 16px);
    animation: sb-partners-scroll 40s linear infinite;
  }
  .sb-partners-slider:hover .sb-partners-track {
    animation-play-state: paused;
  }
  .sb-partners-track > * {
    flex: 0 0 280px;
  }
  @keyframes sb-partners-scroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .sb-partners-track { animation: none; }
  }
`;

export function PartnersGrid({
  title,
  kicker,
  subtitle,
  partners,
  infiniteSlider,
  variant = 'card',
  logoColor = 'mono',
  logoHeight,
  seeAllLabel,
  seeAllHref,
  maxVisible,
  style,
  paddingOuter,
  headingLine,
  paddingInner,
}: PartnersGridProps) {
  const part = useBlockPart();
  const list = asList<Partner>(partners);
  if (list.length === 0) return null;

  const isLogo = variant === 'logoHover';
  const isLogoLabel = variant === 'logoLabel';
  const renderCard = (p: Partner, i: number) =>
    isLogo ? (
      <PartnerLogoCard key={i} p={p} logoColor={logoColor} />
    ) : isLogoLabel ? (
      <PartnerLogoLabelCard key={i} p={p} />
    ) : (
      <PartnerCard key={i} p={p} />
    );

  const label = tx(seeAllLabel);
  // Only a labelled link with a destination renders — a bare arrow or a link
  // to nowhere is worse than no link at all.
  const action =
    label && seeAllHref ? (
      <PortalLink className="sb-partners-seeall" href={seeAllHref}>
        {label}
        <span className="sb-partners-seeall-arrow" aria-hidden="true">
          →
        </span>
      </PortalLink>
    ) : undefined;

  // The shared optical baseline: one box height for every logo in the wall.
  const boxVars = (logoHeight ? { '--sb-logo-box-h': `${logoHeight}px` } : {}) as CSSProperties;

  const shellProps = {
    headingLine,
    style,
    kicker: tx(kicker),
    title: tx(title),
    subtitle: tx(subtitle),
    action,
    paddingOuter,
    paddingInner,
  };

  const styleTags = (
    <>
      {isLogo && <style dangerouslySetInnerHTML={{ __html: LOGO_VARIANT_CSS }} />}
      {isLogoLabel && <style dangerouslySetInnerHTML={{ __html: LOGO_LABEL_VARIANT_CSS }} />}
      {/* Unconditional: the plain `card` variant has no stylesheet of its own,
          but its logo needs the same dark-mode lift as the logo walls. */}
      <style dangerouslySetInnerHTML={{ __html: DARK_LOGO_CSS }} />

      {action && <style dangerouslySetInnerHTML={{ __html: LINK_CSS }} />}
    </>
  );

  if (infiniteSlider) {
    const doubled = [...list, ...list];
    return (
      <SectionShell {...shellProps}>
        <style dangerouslySetInnerHTML={{ __html: SLIDER_CSS }} />
        {styleTags}
        <div className="sb-partners-slider">
          <div {...part('list')} className="sb-partners-track" style={boxVars}>
            {doubled.map(renderCard)}
          </div>
        </div>
      </SectionShell>
    );
  }

  // maxVisible only trims the static grid — the marquee already keeps a long
  // partner list compact by scrolling it.
  const visible = maxVisible && maxVisible > 0 ? list.slice(0, maxVisible) : list;

  return (
    <SectionShell {...shellProps}>
      {styleTags}
      <div
        {...part('list')}
        className={
          isLogo ? 'sb-partners-logogrid' : isLogoLabel ? 'sb-partners-logolabelgrid' : undefined
        }
        style={
          isLogo || isLogoLabel
            ? boxVars
            : {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 'var(--space-4, 16px)',
              }
        }
      >
        {visible.map(renderCard)}
      </div>
    </SectionShell>
  );
}
